import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  FileText, 
  Plus, 
  Search, 
  Filter, 
  Eye, 
  Edit, 
  Trash2, 
  RefreshCw,
  Building2,
  Calendar,
  Mail,
  Paperclip,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  ChevronRight,
  Info,
  Target,
  TrendingUp
} from 'lucide-react';

export const ApplicationsHelpPage: React.FC = () => {
  const navigate = useNavigate();

  const features = [
    {
      id: 'header',
      title: 'Header e Navigazione',
      description: 'La sezione superiore della pagina offre accesso rapido alle funzioni principali.',
      details: [
        'Pulsante "Indietro" per tornare alla dashboard',
        'Titolo della pagina con descrizione',
        'Pulsante "Aggiorna" per ricaricare le candidature',
        'Pulsante "Nuova Candidatura" per aggiungere una candidatura'
      ],
      icon: <FileText className="h-5 w-5" />
    },
    {
      id: 'stats',
      title: 'Statistiche Rapide',
      description: 'Quattro card che mostrano un riepilogo immediato delle tue candidature.',
      details: [
        '**Totale**: Numero complessivo di candidature',
        '**In Attesa**: Candidature ancora in fase di valutazione',
        '**Colloqui**: Candidature con colloquio programmato',
        '**Accettate**: Candidature che hanno avuto esito positivo'
      ],
      icon: <TrendingUp className="h-5 w-5" />
    },
    {
      id: 'search-filters',
      title: 'Ricerca e Filtri',
      description: 'Strumenti potenti per trovare rapidamente le candidature che ti interessano.',
      details: [
        '**Barra di ricerca**: Cerca per nome azienda o posizione lavorativa',
        '**Filtro per stato**: Filtra per stato della candidatura (In attesa, Colloquio, ecc.)',
        '**Filtri attivi**: Visualizza i filtri applicati con possibilità di rimuoverli',
        '**Reset filtri**: Pulsante per rimuovere tutti i filtri applicati'
      ],
      icon: <Search className="h-5 w-5" />
    },
    {
      id: 'cards',
      title: 'Card Candidature',
      description: 'Ogni candidatura è visualizzata in una card moderna e informativa.',
      details: [
        '**Nome azienda**: Cliccabile per aprire i dettagli',
        '**Posizione lavorativa**: Ruolo per cui ti sei candidato',
        '**Stato**: Badge colorato che indica lo stato attuale',
        '**Data invio**: Quando hai inviato la candidatura',
        '**Metodo di contatto**: Come hai contattato l\'azienda'
      ],
      icon: <Building2 className="h-5 w-5" />
    },
    {
      id: 'actions',
      title: 'Azioni sulle Candidature',
      description: 'Tre pulsanti per gestire ogni candidatura.',
      details: [
        '**👁️ Visualizza**: Apre i dettagli completi della candidatura',
        '**✏️ Modifica**: Permette di modificare i dati della candidatura',
        '**🗑️ Elimina**: Rimuove definitivamente la candidatura (con conferma)'
      ],
      icon: <Target className="h-5 w-5" />
    },
    {
      id: 'status-indicators',
      title: 'Indicatori di Stato',
      description: 'Sistema di indicatori per monitorare lo stato delle candidature.',
      details: [
        '**🟡 In attesa**: Candidatura inviata, in attesa di risposta',
        '**🔵 Colloquio programmato**: Hai un colloquio fissato',
        '**🟢 Accettato**: Candidatura accettata con successo',
        '**🔴 Rifiutato**: Candidatura non accettata',
        '**⚫ Scaduto**: Candidatura scaduta o non più valida'
      ],
      icon: <Clock className="h-5 w-5" />
    },
    {
      id: 'priority-indicators',
      title: 'Indicatori di Priorità',
      description: 'Sistema automatico che evidenzia candidature recenti o vecchie.',
      details: [
        '**🟢 Recente**: Candidatura inviata negli ultimi 7 giorni',
        '**🟠 Vecchia**: Candidatura inviata più di 30 giorni fa',
        '**Allegati**: Mostra il numero di documenti allegati',
        '**Note**: Preview delle note aggiunte alla candidatura'
      ],
      icon: <Info className="h-5 w-5" />
    },
    {
      id: 'pagination',
      title: 'Paginazione',
      description: 'Navigazione tra le pagine quando hai molte candidature.',
      details: [
        '**10 candidature per pagina** per una visualizzazione ottimale',
        '**Pulsanti Precedente/Successiva** per navigare',
        '**Contatore pagina** che mostra la posizione corrente',
        '**Disabilitazione automatica** quando si è alla prima/ultima pagina'
      ],
      icon: <Target className="h-5 w-5" />
    }
  ];

  const tips = [
    'Usa la barra di ricerca per trovare rapidamente candidature specifiche',
    'I filtri per stato ti aiutano a concentrarti su candidature attive',
    'Clicca sul nome dell\'azienda per aprire i dettagli completi',
    'Le candidature recenti sono evidenziate automaticamente',
    'Usa il pulsante "Aggiorna" se pensi che ci siano aggiornamenti',
    'Le note ti aiutano a ricordare dettagli importanti di ogni candidatura',
    'Gli allegati mostrano quanti documenti hai inviato per ogni candidatura',
    'Su mobile, le card si adattano automaticamente alla schermata'
  ];

  const workflow = [
    {
      step: 1,
      title: 'Aggiungi una Candidatura',
      description: 'Clicca su "Nuova Candidatura" e compila tutti i campi richiesti'
    },
    {
      step: 2,
      title: 'Monitora lo Stato',
      description: 'Controlla regolarmente lo stato delle tue candidature'
    },
    {
      step: 3,
      title: 'Aggiorna quando Necessario',
      description: 'Modifica lo stato quando ricevi aggiornamenti dall\'azienda'
    },
    {
      step: 4,
      title: 'Gestisci i Colloqui',
      description: 'Quando programmi un colloquio, aggiorna lo stato e vai al calendario'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-2 lg:p-4 lg:pr-8 space-y-4 lg:space-y-6">
      <div className="w-full max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 lg:p-6">
          <div className="flex items-center gap-3 mb-4">
            <button
              onClick={() => navigate('/help')}
              className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-green-100 to-green-200 rounded-xl">
                <FileText className="h-6 w-6 text-green-700" />
              </div>
              <div>
                <h1 className="text-xl lg:text-2xl font-bold text-gray-900">
                  Guida Gestione Candidature
                </h1>
                <p className="text-sm text-gray-600">
                  Scopri come gestire efficacemente tutte le tue candidature di lavoro
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Panoramica */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 lg:p-6">
          <div className="flex items-start gap-3 mb-4">
            <div className="p-2 bg-green-100 rounded-lg">
              <Info className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">
                Panoramica
              </h2>
              <p className="text-gray-600 text-sm leading-relaxed">
                La pagina "Le Mie Candidature" è il tuo centro di controllo per tutte le candidature di lavoro. 
                Qui puoi visualizzare, cercare, filtrare e gestire tutte le tue candidature in un unico posto. 
                La pagina è progettata per essere intuitiva e efficiente, permettendoti di trovare rapidamente 
                le informazioni che ti servono.
              </p>
            </div>
          </div>
        </div>

        {/* Funzionalità */}
        <div className="space-y-4 lg:space-y-6">
          {features.map((feature, index) => (
            <div key={feature.id} className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 lg:p-6">
              <div className="flex items-start gap-3 mb-4">
                <div className="p-2 bg-green-100 rounded-lg">
                  {feature.icon}
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 text-sm mb-3">
                    {feature.description}
                  </p>
                  <ul className="space-y-2">
                    {feature.details.map((detail, detailIndex) => (
                      <li key={detailIndex} className="flex items-start gap-2 text-sm text-gray-600">
                        <ChevronRight className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                        <span dangerouslySetInnerHTML={{ __html: detail }} />
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Workflow */}
        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl border border-green-200 p-4 lg:p-6">
          <h3 className="text-lg font-semibold text-green-900 mb-4 flex items-center gap-2">
            <Target className="h-5 w-5" />
            Workflow Consigliato
          </h3>
          <div className="space-y-3">
            {workflow.map((step, index) => (
              <div key={index} className="flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                  {step.step}
                </div>
                <div>
                  <h4 className="font-semibold text-green-900 text-sm">{step.title}</h4>
                  <p className="text-green-800 text-sm">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Suggerimenti */}
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl border border-blue-200 p-4 lg:p-6">
          <h3 className="text-lg font-semibold text-blue-900 mb-4 flex items-center gap-2">
            <Info className="h-5 w-5" />
            Suggerimenti Utili
          </h3>
          <ul className="space-y-2">
            {tips.map((tip, index) => (
              <li key={index} className="flex items-start gap-2 text-sm text-blue-800">
                <div className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 flex-shrink-0"></div>
                <span>{tip}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Azioni */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 lg:p-6">
          <div className="text-center">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Pronto a gestire le tue candidature?
            </h3>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => navigate('/applications')}
                className="flex items-center justify-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
              >
                <FileText className="h-4 w-4" />
                Vai alle Candidature
              </button>
              <button
                onClick={() => navigate('/applications/new')}
                className="flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                <Plus className="h-4 w-4" />
                Nuova Candidatura
              </button>
              <button
                onClick={() => navigate('/help')}
                className="flex items-center justify-center gap-2 px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
              >
                <FileText className="h-4 w-4" />
                Altre Guide
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}; 